import { IndexedDBBlocksOptions } from './types';
import { reqToPromise } from './utils';

/**
 * IndexedDBBlocks provides an abstraction for managing IndexedDB databases,
 * enabling operations such as creating or upgrading stores, setting, and retrieving data.
 * It supports both simple and compound keys for database operations.
 */
export class IndexedDBBlocks {
  private stateStore: string;
  private name: string;
  private version: number;
  private db: any;
  private openPromise: Promise<any> | null = null;
  private extraStores: Array<{ name: string; keyPath: string | string[] }>;

  constructor(options: IndexedDBBlocksOptions = {name: 'blocks-db'}) {
    this.name = options.name;
    this.version = options.version || 1;
    this.stateStore = options.stateStore || '__state';
    this.extraStores = options.extraStores || [];
    this.db = null;
  }

  // open and create stores + indexes on upgrade
  async open() {
    if (this.db) return this.db;
    if (!this.openPromise) {
      const req = indexedDB.open(this.name, this.version);
      req.onupgradeneeded = (ev) => this._upgrade(ev);
      // another connection (e.g. an old tab on the previous schema version) blocks the upgrade
      req.onblocked = () => console.warn(`IndexedDB ${this.name}: upgrade blocked by another open connection, close other tabs`);
      this.openPromise = reqToPromise(req).then((db: any) => {
        // a newer version is being opened elsewhere: release our connection so the upgrade can proceed
        db.onversionchange = () => {
          db.close();
          this.db = null;
          this.openPromise = null;
        };
        this.db = db;
        return db;
      }).catch((e) => {
        this.openPromise = null;
        throw e;
      });
    }
    return this.openPromise;
  }

  _upgrade(ev) {
    const db = ev.target.result;
    // ensure state store exists
    if (!db.objectStoreNames.contains(this.stateStore)) {
      db.createObjectStore(this.stateStore, { keyPath: ['sessionId', 'athleteId'], unique: true });
    }
    // additional stores (existing data in the other stores is kept)
    for (const store of this.extraStores) {
      if (!db.objectStoreNames.contains(store.name)) {
        db.createObjectStore(store.name, { keyPath: store.keyPath });
      }
    }
  }

  // Close DB
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.openPromise = null;
  }

  // get by primary key
  async get(key: string | any[]) {
    const db = await this.open();
    const tx = db.transaction([this.stateStore], 'readonly');
    const store = tx.objectStore(this.stateStore);
    // Handle both simple keys and compound keys
    if (Array.isArray(key)) {
      // For compound keys, we need to query by the compound key
      return await reqToPromise(store.get(key));
    } else {
      // For simple keys, use the original approach
      return await reqToPromise(store.get(key));
    }
  }

  async exists( key: string | any[] ): Promise<boolean> {
    const db = await this.open();
    const tx = db.transaction([this.stateStore], 'readonly');
    const store = tx.objectStore(this.stateStore);
    const result = await reqToPromise(store.get(key));
    return result !== undefined;
  }

  async getAll(key?: string | any[] | IDBKeyRange) {
    const db = await this.open();
    const tx = db.transaction([this.stateStore], 'readonly');
    const store = tx.objectStore(this.stateStore);
    if (key) {
      return await reqToPromise(store.getAll(key));
    } else {
      return await reqToPromise(store.getAll());
    }
  }

  async set(key: string | any[], data: any) {
    if (!key) throw new Error('key');
    const db = await this.open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([this.stateStore], 'readwrite');
      const store = tx.objectStore(this.stateStore);
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
      // Handle both simple keys and compound keys
      let record;
      if (Array.isArray(key)) {
        // For compound keys, create an object with sessionId and athleteId
        record = {
          sessionId: key[0],
          athleteId: key[1],
          data,
          updatedAt: Date.now()
        };
      } else {
        // For simple keys, use the original format
        record = {
          id: key,
          data,
          updatedAt: Date.now()
        };
      }

      store.put(record);
    });
  }

  // generic helpers on any store (used for the manifest store)
  async getFrom(store: string, key: any) {
    const db = await this.open();
    const tx = db.transaction([store], 'readonly');
    return await reqToPromise(tx.objectStore(store).get(key));
  }

  async putTo(store: string, record: any) {
    const db = await this.open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([store], 'readwrite');
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e: any) => reject(e.target.error);
      tx.onabort = (e: any) => reject(e.target.error || new Error('IDB transaction aborted'));
      tx.objectStore(store).put(record);
    });
  }

  async deleteFrom(store: string, key: any) {
    const db = await this.open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction([store], 'readwrite');
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e: any) => reject(e.target.error);
      tx.onabort = (e: any) => reject(e.target.error || new Error('IDB transaction aborted'));
      tx.objectStore(store).delete(key);
    });
  }

  // primary keys of the state store within a range (no data read)
  async getAllKeys(range: IDBKeyRange) {
    const db = await this.open();
    const tx = db.transaction([this.stateStore], 'readonly');
    return await reqToPromise(tx.objectStore(this.stateStore).getAllKeys(range));
  }

  async deleteKey(key: any[] | IDBKeyRange) {
    return this.deleteFrom(this.stateStore, key);
  }
}
